import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Ticket, RefreshCw, Mail, Sparkles, Loader2, Dice5, Flame, Snowflake,
  Shuffle, Zap, Calendar, TrendingUp, AlertTriangle, Plus, Star,
  CheckCircle2, XCircle, Clock, History, Trophy, Trash2, BarChart3,
  Search, Bell,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAD = (n) => String(n).padStart(2, '0');

// ---------------------------------------------------------------------------
// Ithuba-style colour scheme: 1-13 red · 14-25 yellow · 26-37 green ·
// 38-49 blue · 50+ navy. Bonus/Powerball balls are styled red.
// ---------------------------------------------------------------------------
const ballColor = (n, isBonus = false) => {
  if (isBonus) return '#dc2626';
  if (n >= 1 && n <= 13) return '#dc2626';
  if (n >= 14 && n <= 25) return '#eab308';
  if (n >= 26 && n <= 37) return '#16a34a';
  if (n >= 38 && n <= 49) return '#2563eb';
  return '#1e3a8a';
};

const STRATEGIES = [
  { id: 'random', label: 'Random', icon: Shuffle, desc: 'Pure uniform random — no statistical bias.' },
  { id: 'hot', label: 'Hot', icon: Flame, desc: 'Weighted toward the most-drawn numbers in the last 50 draws.' },
  { id: 'cold', label: 'Cold', icon: Snowflake, desc: 'Contrarian — weighted toward the least-drawn numbers.' },
  { id: 'balanced', label: 'Balanced', icon: Zap, desc: 'Mix of hot and random — recommended default.' },
];

const GAME_EMOJI = { 'lotto': '🎯', 'lotto-plus-1': '➕', 'lotto-plus-2': '⭐', 'powerball': '⚡', 'powerball-plus': '💫' };

// ---------------------------------------------------------------------------
// Ball — Ithuba-style 3D ball with radial gradient + drop shadow + zero pad
// ---------------------------------------------------------------------------
const Ball = ({ n, isBonus = false, size = 44, ringTitle = null }) => {
  const color = ballColor(n, isBonus);
  return (
    <span
      title={ringTitle || (isBonus ? 'Bonus' : `${n}`)}
      className="inline-flex items-center justify-center rounded-full font-extrabold text-white relative"
      style={{
        width: size, height: size,
        background: `radial-gradient(circle at 30% 28%, rgba(255,255,255,0.55) 0%, ${color} 55%, rgba(0,0,0,0.20) 100%)`,
        fontSize: size * 0.42,
        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
        boxShadow: '0 3px 6px rgba(0,0,0,0.20), inset 0 -2px 4px rgba(0,0,0,0.18)',
        fontFamily: 'Arial, sans-serif',
        letterSpacing: '-0.5px',
      }}
      data-testid={`lottogen-ball-${n}`}
    >
      {PAD(n)}
    </span>
  );
};

const fmtR = (v) => (v == null ? '—' : `R ${Math.round(v).toLocaleString()}`);

const StatusBadge = ({ status, tier }) => {
  if (status === 'won') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white">
      <Trophy className="w-3 h-3" />WON {tier ? `· ${tier.split('—')[0].trim()}` : ''}
    </span>
  );
  if (status === 'lost') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500 text-white">
      <XCircle className="w-3 h-3" />LOST
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600">
      <Clock className="w-3 h-3" />PENDING
    </span>
  );
};

// ---------------------------------------------------------------------------
// Draw card — bold "DRAW RESULTS" header + balls + jackpot, modeled on the
// Ithuba-style image the user supplied.
// ---------------------------------------------------------------------------
const DrawCard = ({ row, game, spec }) => {
  const isPowerball = spec.family === 'powerball';
  const headerBg = isPowerball
    ? 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #1e3a8a 100%)'
    : 'linear-gradient(135deg, #b91c1c 0%, #dc2626 50%, #b91c1c 100%)';
  const bonusKey = isPowerball ? 'powerball' : 'bonus';
  const bonusLabel = spec.bonus_label || (isPowerball ? 'POWERBALL' : 'BONUS');
  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-white" data-testid={`lottogen-draw-${row.draw_id}`}>
      {/* Banner */}
      <div className="py-2 px-3 text-white text-center font-extrabold tracking-wider text-xs" style={{ background: headerBg }}>
        {spec.label.toUpperCase()} · DRAW RESULTS · #{row.draw_id}
      </div>
      <div className="p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-slate-500">
          <Calendar className="w-3 h-3 inline mr-1" />
          {new Date(row.date_iso).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase text-slate-400 tracking-wide">Jackpot</div>
          <div className="font-bold text-sm text-slate-800">{fmtR(row.jackpot_zar)}</div>
        </div>
      </div>
      <div className="px-3 pb-4 flex items-center gap-1.5 flex-wrap justify-center">
        {(row.numbers || []).map((n) => <Ball key={n} n={n} size={42} />)}
        {row[bonusKey] != null && (
          <>
            <div className="mx-1 text-slate-300 font-bold text-lg">+</div>
            <div className="flex flex-col items-center">
              <Ball n={row[bonusKey]} isBonus size={42} />
              <div className="text-[8px] uppercase text-slate-400 tracking-wide mt-1">{bonusLabel}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PickedTicket = ({ ticket, spec, idx }) => {
  const isPB = spec.family === 'powerball';
  const bonusKey = isPB ? 'powerball' : 'bonus';
  return (
    <div className="bg-slate-50 border-l-4 border-teal-500 p-3 rounded flex items-center gap-2 flex-wrap" data-testid={`lottogen-ticket-${idx}`}>
      <span className="text-[10px] text-slate-400 font-semibold w-6">#{idx + 1}</span>
      <div className="flex gap-1.5 items-center">
        {(ticket.numbers || []).map((n) => <Ball key={n} n={n} size={34} />)}
      </div>
      <span className="text-slate-300 mx-1">+</span>
      <Ball n={ticket[bonusKey]} isBonus size={34} />
      <span className="text-[10px] text-slate-400 uppercase tracking-wide ml-1">{isPB ? 'PB' : 'Bonus'}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lucky-Dip form — user types in their played numbers + a draw date and the
// tool tells them WON / LOST / PENDING after the corresponding draw fetches.
// ---------------------------------------------------------------------------
const LuckyDipForm = ({ game, spec, onAdded }) => {
  const [numbers, setNumbers] = useState('');
  const [bonus, setBonus] = useState('');
  const [playedDate, setPlayedDate] = useState(new Date().toISOString().slice(0, 10));
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const nums = numbers.split(/[\s,]+/).filter(Boolean).map(Number).filter((n) => !isNaN(n));
    if (nums.length !== spec.main_count) {
      toast.error(`Need exactly ${spec.main_count} numbers for ${spec.label}`);
      return;
    }
    const bonusN = bonus ? Number(bonus) : null;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/lucky-dip`, {
        game, numbers: nums, bonus: bonusN, played_for_date: playedDate, label,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      toast.success('Lucky-dip saved');
      setNumbers(''); setBonus(''); setLabel('');
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500">Your {spec.main_count} numbers (space-separated)</Label>
        <Input value={numbers} onChange={(e) => setNumbers(e.target.value)} placeholder={spec.family === 'powerball' ? '5 12 23 34 47' : '6 14 22 33 41 49'} data-testid="lottogen-luckydip-numbers" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">{spec.bonus_label}</Label>
        <Input type="number" min={spec.bonus_range[0]} max={spec.bonus_range[1]} value={bonus} onChange={(e) => setBonus(e.target.value)} data-testid="lottogen-luckydip-bonus" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Played for date</Label>
        <Input type="date" value={playedDate} onChange={(e) => setPlayedDate(e.target.value)} data-testid="lottogen-luckydip-date" />
      </div>
      <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 w-full md:w-auto" data-testid="lottogen-luckydip-save">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
        Save ticket
      </Button>
      <div className="md:col-span-5">
        <Label className="text-xs text-slate-500">Label (optional)</Label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Family ticket / Wedding anniversary numbers" data-testid="lottogen-luckydip-label" />
      </div>
    </div>
  );
};

const LuckyDipRow = ({ ticket, spec, onDelete }) => {
  const bonusKey = spec.family === 'powerball' ? 'powerball' : 'bonus';
  return (
    <div className="border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`lottogen-luckydip-${ticket.id}`}>
      <div className="flex gap-1 items-center">
        {(ticket.numbers || []).map((n) => <Ball key={n} n={n} size={32} />)}
        <span className="text-slate-300 mx-1">+</span>
        {ticket.bonus != null && <Ball n={ticket.bonus} isBonus size={32} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500">For draw on <b className="text-slate-700">{ticket.played_for_date}</b>{ticket.label ? ` · ${ticket.label}` : ''}</div>
        {ticket.main_matches != null && (
          <div className="text-[10px] text-slate-500 mt-0.5">
            Matched <b>{ticket.main_matches}</b> mains{ticket.bonus_match ? ' + bonus' : ''}{ticket.prize_tier ? ` · ${ticket.prize_tier}` : ''}
          </div>
        )}
      </div>
      <StatusBadge status={ticket.status} tier={ticket.prize_tier} />
      <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400 hover:text-rose-600" onClick={() => onDelete(ticket.id)} data-testid={`lottogen-luckydip-delete-${ticket.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Check Ticket — instant match against a chosen draw (default = latest)
// ---------------------------------------------------------------------------
const CheckTicketPanel = ({ game, spec, onTrackAdded }) => {
  const [numbers, setNumbers] = useState('');
  const [bonus, setBonus] = useState('');
  const [drawDate, setDrawDate] = useState(''); // '' = latest
  const [drawDates, setDrawDates] = useState([]);
  const [label, setLabel] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  // Reset whenever the active game changes
  useEffect(() => {
    setNumbers(''); setBonus(''); setDrawDate(''); setResult(null); setLabel(''); setNotifyEmail('');
  }, [game]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/draw-dates?game=${game}&limit=60`, { headers: authHeaders() });
        setDrawDates(r.data?.draws || []);
      } catch { /* ignore */ }
    })();
  }, [game]);

  const parseNumbers = () => numbers.split(/[\s,]+/).filter(Boolean).map(Number).filter((n) => !isNaN(n));

  const check = async () => {
    const nums = parseNumbers();
    if (nums.length !== spec.main_count) {
      toast.error(`Need exactly ${spec.main_count} numbers for ${spec.label}`);
      return;
    }
    const body = { game, numbers: nums, bonus: bonus ? Number(bonus) : null };
    if (drawDate) body.draw_date = drawDate;
    setChecking(true);
    setResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/check-ticket`, body, { headers: authHeaders() });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const track = async () => {
    const nums = parseNumbers();
    if (nums.length !== spec.main_count) {
      toast.error(`Need exactly ${spec.main_count} numbers for ${spec.label}`);
      return;
    }
    setTracking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/tracked`, {
        game, numbers: nums, bonus: bonus ? Number(bonus) : null, label,
        notify_email: notifyEmail.trim() || null,
      }, { headers: authHeaders() });
      toast.success(notifyEmail.trim()
        ? `Tracking + will email ${notifyEmail.trim()} on every win`
        : 'Ticket is now being tracked against future draws');
      onTrackAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save tracked ticket');
    } finally {
      setTracking(false);
    }
  };

  const bonusKey = spec.family === 'powerball' ? 'powerball' : 'bonus';
  const matchedSet = new Set(result?.result?.matched_numbers || []);

  return (
    <Card data-testid="lottogen-checkticket-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-teal-600" />
          <h3 className="font-semibold text-slate-800 text-sm">Check my ticket — {spec.label}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-3">
            <Label className="text-xs text-slate-500">Your {spec.main_count} numbers (space or comma separated)</Label>
            <Input
              value={numbers}
              onChange={(e) => setNumbers(e.target.value)}
              placeholder={spec.family === 'powerball' ? 'e.g. 5 12 23 34 47' : 'e.g. 6 14 22 33 41 49'}
              data-testid="lottogen-checkticket-numbers"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">{spec.bonus_label}</Label>
            <Input
              type="number"
              min={spec.bonus_range[0]}
              max={spec.bonus_range[1]}
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
              placeholder="optional"
              data-testid="lottogen-checkticket-bonus"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-500">Draw to check against</Label>
            <select
              value={drawDate}
              onChange={(e) => setDrawDate(e.target.value)}
              className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm bg-white"
              data-testid="lottogen-checkticket-date"
            >
              <option value="">Latest draw{drawDates[0] ? ` · #${drawDates[0].draw_id} (${drawDates[0].date_iso})` : ''}</option>
              {drawDates.map((d) => (
                <option key={d.draw_id} value={d.date_iso}>#{d.draw_id} · {d.date_iso}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={check} disabled={checking} className="bg-teal-600 hover:bg-teal-700" data-testid="lottogen-checkticket-btn">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Check now
          </Button>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Optional label for tracking (e.g. Family ticket)"
            className="flex-1 min-w-[200px]"
            data-testid="lottogen-checkticket-label"
          />
          <Button onClick={track} disabled={tracking} variant="outline" className="border-teal-600 text-teal-700 hover:bg-teal-50" data-testid="lottogen-checkticket-track-btn">
            {tracking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Save & track future draws
          </Button>
        </div>

        {/* Notify-by-email row — collapsed visually under the actions */}
        <div className="flex items-center gap-2 pl-1">
          <Bell className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
          <Label className="text-xs text-slate-500 whitespace-nowrap">Email me on every win (optional)</Label>
          <Input
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="you@example.com — leave blank to disable"
            className="flex-1 max-w-md h-8 text-xs"
            data-testid="lottogen-checkticket-notify-email"
          />
        </div>

        {result && (
          <div
            className={`rounded-lg border p-4 ${result.result.won ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}
            data-testid="lottogen-checkticket-result"
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="font-bold text-sm text-slate-800">
                {result.result.won ? (
                  <span className="text-emerald-700 inline-flex items-center gap-1"><Trophy className="w-4 h-4" /> WINNER · {result.result.prize_tier}</span>
                ) : (
                  <span className="text-slate-500 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> Not a prize-tier match</span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                vs draw #{result.draw.draw_id} · {result.draw.date_iso}
              </div>
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Your ticket</div>
                <div className="flex gap-1 items-center flex-wrap">
                  {(result.ticket.numbers || []).map((n) => (
                    <div key={n} className="relative">
                      <Ball n={n} size={36} />
                      {matchedSet.has(n) && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute -top-1 -right-1 bg-white rounded-full" />
                      )}
                    </div>
                  ))}
                  {result.ticket.bonus != null && (
                    <>
                      <span className="text-slate-300 mx-1">+</span>
                      <div className="relative">
                        <Ball n={result.ticket.bonus} isBonus size={36} />
                        {result.result.bonus_match && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 absolute -top-1 -right-1 bg-white rounded-full" />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Winning numbers</div>
                <div className="flex gap-1 items-center flex-wrap">
                  {(result.draw.numbers || []).map((n) => (
                    <Ball key={n} n={n} size={36} />
                  ))}
                  {result.draw.bonus != null && (
                    <>
                      <span className="text-slate-300 mx-1">+</span>
                      <Ball n={result.draw.bonus} isBonus size={36} />
                    </>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-600 pt-1">
                Matched <b>{result.result.main_matches}</b> of {spec.main_count} main numbers
                {result.result.bonus_match ? <> + <b>{spec.bonus_label}</b></> : null}
                {result.draw.jackpot_zar ? <> · jackpot was <b>R{Math.round(result.draw.jackpot_zar).toLocaleString()}</b></> : null}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ---------------------------------------------------------------------------
// Tracked tickets — auto-evaluated against every future draw
// ---------------------------------------------------------------------------
const TrackedTicketRow = ({ ticket, spec, onDelete, onPatch }) => {
  const best = ticket.best_result;
  const lastCheck = (ticket.history || [])[0];
  const [editing, setEditing] = useState(false);
  const [emailDraft, setEmailDraft] = useState(ticket.notify_email || '');
  const [saving, setSaving] = useState(false);

  const submitEmail = async (next) => {
    setSaving(true);
    try {
      await onPatch?.(ticket.id, { notify_email: next });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`lottogen-tracked-${ticket.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 items-center">
          {(ticket.numbers || []).map((n) => <Ball key={n} n={n} size={28} />)}
          {ticket.bonus != null && (
            <>
              <span className="text-slate-300 mx-0.5">+</span>
              <Ball n={ticket.bonus} isBonus size={28} />
            </>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500">
            {ticket.label ? <b className="text-slate-700">{ticket.label}</b> : <span className="text-slate-400 italic">No label</span>}
            <span className="text-slate-400 ml-2">· tracking since {ticket.created_at?.slice(0, 10)}</span>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {ticket.draws_checked || 0} draws checked
            {(ticket.wins || 0) > 0 && <> · <b className="text-emerald-600">{ticket.wins} win{ticket.wins === 1 ? '' : 's'}</b></>}
            {best?.prize_tier && <> · best: <b className="text-emerald-700">{best.prize_tier}</b> on {best.date_iso}</>}
            {!best && lastCheck && <> · last: {lastCheck.main_matches} match{lastCheck.main_matches === 1 ? '' : 'es'} on {lastCheck.date_iso}</>}
          </div>
        </div>

        {/* Notification status / toggle */}
        {!editing ? (
          ticket.notify_email ? (
            <button
              onClick={() => { setEmailDraft(ticket.notify_email); setEditing(true); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
              title="Notifications enabled · click to edit"
              data-testid={`lottogen-tracked-notify-on-${ticket.id}`}
            >
              <Bell className="w-3 h-3" />
              <span className="max-w-[140px] truncate">{ticket.notify_email}</span>
            </button>
          ) : (
            <button
              onClick={() => { setEmailDraft(''); setEditing(true); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
              title="Add a win-notification email"
              data-testid={`lottogen-tracked-notify-off-${ticket.id}`}
            >
              <Bell className="w-3 h-3" /> No alerts
            </button>
          )
        ) : (
          <div className="inline-flex items-center gap-1" data-testid={`lottogen-tracked-notify-edit-${ticket.id}`}>
            <Input
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder="email or empty to disable"
              className="h-7 text-xs w-44"
              autoFocus
            />
            <Button size="sm" className="h-7 px-2 bg-teal-600 hover:bg-teal-700" disabled={saving} onClick={() => submitEmail(emailDraft.trim())} data-testid={`lottogen-tracked-notify-save-${ticket.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400" onClick={() => setEditing(false)}>×</Button>
          </div>
        )}

        <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400 hover:text-rose-600" onClick={() => onDelete(ticket.id)} data-testid={`lottogen-tracked-delete-${ticket.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AdminLottoGen = () => {
  const [games, setGames] = useState([]);
  const [game, setGame] = useState('lotto');
  const [draws, setDraws] = useState([]);
  const [topNumbers, setTopNumbers] = useState([]);
  const [stats, setStats] = useState(null);
  const [strategy, setStrategy] = useState('balanced');
  const [ticketCount, setTicketCount] = useState(5);
  const [generatedTickets, setGeneratedTickets] = useState([]);
  const [luckyDips, setLuckyDips] = useState([]);
  const [trackedTickets, setTrackedTickets] = useState([]);
  const [pickHistory, setPickHistory] = useState({ batches: [], aggregate: [] });
  const [activeTab, setActiveTab] = useState('results'); // results | check | luckydip | history
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const spec = useMemo(() => games.find((g) => g.id === game) || null, [games, game]);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/games`, { headers: authHeaders() });
      setGames(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadGameData = useCallback(async (g) => {
    try {
      const [d, t, s, l, h, tr] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/draws?game=${g}&limit=20`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/top-numbers?game=${g}&window=50&top_n=6`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/stats`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/lucky-dip?game=${g}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/pick-history?game=${g}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/lotto-gen/tracked?game=${g}`, { headers: authHeaders() }),
      ]);
      setDraws(d.data?.draws || []);
      setTopNumbers(t.data?.top || []);
      setStats(s.data);
      setLuckyDips(l.data || []);
      setPickHistory(h.data || { batches: [], aggregate: [] });
      setTrackedTickets(tr.data || []);
    } catch (err) {
      toast.error('Failed to load Lotto-Gen data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => {
    setLoading(true);
    setGeneratedTickets([]);
    loadGameData(game);
  }, [game, loadGameData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/refresh`, {}, { headers: authHeaders() });
      const totalNew = Object.values(r.data || {}).reduce((acc, g) => acc + (g.inserted || 0), 0);
      toast.success(`Refreshed all 5 games · ${totalNew} new draws`);
      await loadGameData(game);
    } catch (err) {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/generate`, {
        game, strategy, count: ticketCount,
      }, { headers: authHeaders() });
      setGeneratedTickets(r.data?.tickets || []);
      // refresh history so the new batch shows up
      const h = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/pick-history?game=${game}`, { headers: authHeaders() });
      setPickHistory(h.data || { batches: [], aggregate: [] });
    } catch (err) {
      toast.error('Pick generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    setDigestResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/lotto-gen/send-digest`, {}, { headers: authHeaders() });
      setDigestResult(r.data);
      if (r.data?.ok) toast.success(`Digest sent via ${r.data.provider} · status ${r.data.status_code}`);
      else toast.error(`Digest failed: ${r.data?.sendgrid_body || r.data?.error || 'unknown'}`);
    } catch (err) {
      toast.error('Digest send failed');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleDeleteLuckyDip = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/lotto-gen/lucky-dip/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      const l = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/lucky-dip?game=${game}`, { headers: authHeaders() });
      setLuckyDips(l.data || []);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteTracked = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/lotto-gen/tracked/${id}`, { headers: authHeaders() });
      toast.success('Stopped tracking');
      const tr = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/tracked?game=${game}`, { headers: authHeaders() });
      setTrackedTickets(tr.data || []);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handlePatchTracked = async (id, patch) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/lotto-gen/tracked/${id}`, patch, { headers: authHeaders() });
      const next = patch.notify_email;
      toast.success(next ? `Win alerts will go to ${next}` : 'Win alerts disabled');
      const tr = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/tracked?game=${game}`, { headers: authHeaders() });
      setTrackedTickets(tr.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
      throw err;
    }
  };

  const reloadTracked = async () => {
    try {
      const tr = await axios.get(`${BACKEND_URL}/api/admin/lotto-gen/tracked?game=${game}`, { headers: authHeaders() });
      setTrackedTickets(tr.data || []);
    } catch { /* ignore */ }
  };

  const latestStats = stats?.[game];

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-lottogen-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Ticket className="w-6 h-6 text-teal-600" />
              Lotto-Gen
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              SA Lotto, Lotto Plus 1 & 2, Powerball, Powerball Plus · weekly digest to <code className="bg-slate-100 px-1 rounded">sales@brainwave-group.com</code> every Sunday 07:00 SAST
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline" data-testid="lottogen-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh draws
            </Button>
            <Button onClick={handleSendDigest} disabled={sendingDigest} className="bg-teal-600 hover:bg-teal-700" data-testid="lottogen-digest-btn">
              {sendingDigest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send digest now
            </Button>
          </div>
        </div>

        {/* Game selector */}
        <div className="flex gap-2 flex-wrap border-b border-slate-200" data-testid="lottogen-game-tabs">
          {games.map((g) => (
            <button
              key={g.id}
              onClick={() => setGame(g.id)}
              className={`px-3 py-2 font-semibold text-sm transition border-b-2 ${game === g.id
                ? 'text-slate-800 border-teal-600'
                : 'text-slate-400 border-transparent hover:text-slate-600'}`}
              data-testid={`lottogen-tab-${g.id}`}
            >
              {GAME_EMOJI[g.id] || ''} {g.label}
            </button>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-xs">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><b>Picks are statistical curiosities, not predictions.</b> Lottery draws are independent — past results have no influence on future ones. Play responsibly.</span>
        </div>

        {/* Stats */}
        {latestStats && spec && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lottogen-stats">
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Draws cached</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{latestStats.draws_cached}</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Latest draw</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">#{latestStats.latest_draw_id ?? '—'}</div>
              <div className="text-xs text-slate-500">{latestStats.latest_date}</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Format</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{spec.main_count} + {spec.bonus_label[0]}</div>
              <div className="text-xs text-slate-500">{spec.main_range[0]}-{spec.main_range[1]} mains</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Next digest</div>
              <div className="text-sm font-semibold text-slate-800 mt-1">Sunday · 07:00 SAST</div>
              <div className="text-xs text-slate-500">to sales@brainwave-group.com</div>
            </CardContent></Card>
          </div>
        )}

        {/* Email error banner */}
        {digestResult && !digestResult.ok && (
          <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 text-rose-900 rounded-lg p-3 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div><b>Digest send failed</b> — {digestResult?.sendgrid_body || digestResult?.error || 'unknown'}</div>
          </div>
        )}

        {/* Section tabs */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="lottogen-section-tabs">
          {[
            ['results', 'Results & Picks', BarChart3],
            ['check', `Check & Track · ${trackedTickets.length}`, Search],
            ['luckydip', `Lucky Dip · ${luckyDips.length}`, Ticket],
            ['history', `Pick History · ${pickHistory.batches.length}`, History],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-3 py-2 text-xs font-semibold transition border-b-2 flex items-center gap-1.5 ${activeTab === id
                ? 'text-teal-700 border-teal-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'}`}
              data-testid={`lottogen-section-${id}`}
            >
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>

        {loading || !spec ? (
          <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading…</div>
        ) : activeTab === 'results' ? (
          <>
            {/* Latest draw — hero card */}
            {draws[0] && (
              <DrawCard row={draws[0]} game={game} spec={spec} />
            )}

            {/* Top 6 most-drawn numbers */}
            <Card data-testid="lottogen-top-numbers">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">Top 6 most-drawn numbers — last 50 {spec.label} draws</h3>
                </div>
                {topNumbers.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4">No data — refresh draws.</div>
                ) : (
                  <div className="flex gap-6 flex-wrap justify-center sm:justify-start">
                    {topNumbers.map((t) => (
                      <div key={t.number} className="text-center">
                        <Ball n={t.number} size={56} />
                        <div className="text-[11px] font-semibold text-slate-800 mt-2">{t.count} draws</div>
                        <div className="text-[10px] text-slate-500">{t.frequency_pct}%</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generator */}
            <Card data-testid="lottogen-generator">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Dice5 className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">Pick Generator</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="lottogen-strategy-chips">
                  {STRATEGIES.map(({ id, label, icon: Icon, desc }) => (
                    <button key={id} onClick={() => setStrategy(id)} className={`px-3 py-2 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 border ${strategy === id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} title={desc} data-testid={`lottogen-strategy-${id}`}>
                      <Icon className="w-3.5 h-3.5" />{label}
                    </button>
                  ))}
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs text-slate-500">How many tickets?</Label>
                    <Input type="number" min="1" max="20" value={ticketCount} onChange={(e) => setTicketCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))} className="w-24" data-testid="lottogen-ticket-count" />
                  </div>
                  <Button onClick={handleGenerate} disabled={generating} className="bg-teal-600 hover:bg-teal-700" data-testid="lottogen-generate-btn">
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate {ticketCount} ticket{ticketCount === 1 ? '' : 's'}
                  </Button>
                  <div className="text-[10px] text-slate-400 italic ml-auto max-w-xs text-right">
                    {STRATEGIES.find((s) => s.id === strategy)?.desc}
                  </div>
                </div>
                {generatedTickets.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="lottogen-tickets-list">
                    {generatedTickets.map((t, i) => <PickedTicket key={i} ticket={t} spec={spec} idx={i} />)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Draw grid — remaining 19 */}
            <div>
              <h3 className="font-semibold text-slate-800 text-sm mb-3">Previous {Math.min(draws.length - 1, 19)} draws</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lottogen-draws-grid">
                {draws.slice(1).map((r) => (
                  <DrawCard key={r.draw_id} row={r} game={game} spec={spec} />
                ))}
              </div>
            </div>
          </>
        ) : activeTab === 'check' ? (
          <>
            <CheckTicketPanel game={game} spec={spec} onTrackAdded={reloadTracked} />

            <Card data-testid="lottogen-tracked-list-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">
                    Tracked tickets — {spec.label}
                    <span className="text-slate-400 font-normal text-xs ml-1">· auto-checked against every future draw</span>
                  </h3>
                </div>
                {trackedTickets.length === 0 ? (
                  <div className="text-center py-6 text-sm text-slate-400">
                    No tickets being tracked yet. Use the form above and click <b>Save &amp; track future draws</b> to add one.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="lottogen-tracked-list">
                    {trackedTickets.map((t) => (
                      <TrackedTicketRow key={t.id} ticket={t} spec={spec} onDelete={handleDeleteTracked} onPatch={handlePatchTracked} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : activeTab === 'luckydip' ? (
          <>
            <Card data-testid="lottogen-luckydip-form">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ticket className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">Play a Lucky Dip — record your {spec.label} ticket</h3>
                </div>
                <LuckyDipForm game={game} spec={spec} onAdded={() => loadGameData(game)} />
              </CardContent>
            </Card>
            <div className="space-y-2">
              {luckyDips.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-slate-400 text-sm">
                  No lucky-dip tickets yet for {spec.label}. Use the form above to save one — it'll auto-check against the upcoming draw.
                </CardContent></Card>
              ) : (
                luckyDips.map((t) => <LuckyDipRow key={t.id} ticket={t} spec={spec} onDelete={handleDeleteLuckyDip} />)
              )}
            </div>
          </>
        ) : (
          /* Pick history tab */
          <>
            {/* Win-rate per strategy */}
            <Card data-testid="lottogen-strategy-winrate">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">Strategy win-rates — {spec.label}</h3>
                </div>
                {pickHistory.aggregate.length === 0 ? (
                  <p className="text-sm text-slate-400">No evaluated batches yet. Generate some picks and wait for the next draw to fetch.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {pickHistory.aggregate.map((a) => (
                      <div key={a.strategy} className="bg-slate-50 rounded-lg p-3 text-center" data-testid={`lottogen-winrate-${a.strategy}`}>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{a.strategy}</div>
                        <div className="text-2xl font-bold text-teal-700 mt-1">{a.win_rate_pct}%</div>
                        <div className="text-[10px] text-slate-500 mt-1">{a.wins}/{a.tickets} tickets · {a.batches} batches</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Batch list */}
            <div className="space-y-2" data-testid="lottogen-history-list">
              {pickHistory.batches.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-slate-400 text-sm">
                  No generated batches recorded for {spec.label} yet. Switch to "Results & Picks" and click "Generate".
                </CardContent></Card>
              ) : (
                pickHistory.batches.slice(0, 15).map((b) => (
                  <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`lottogen-history-${b.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="text-xs">
                        <b className="text-slate-800 uppercase">{b.strategy}</b>
                        <span className="text-slate-400 ml-2">{new Date(b.generated_at).toLocaleString()}</span>
                        {b.matched_draw_date && (
                          <span className="text-slate-500 ml-2">vs draw on {b.matched_draw_date} (#{b.matched_draw_id})</span>
                        )}
                      </div>
                      {b.evaluated ? (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${b.wins_in_batch > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-700'}`}>
                          {b.wins_in_batch}/{b.tickets_in_batch} tickets won
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-slate-200 text-slate-600">Awaiting draw</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {(b.ticket_results || b.tickets || []).slice(0, 5).map((t, i) => {
                        const tier = t.prize_tier;
                        return (
                          <div key={i} className="flex items-center gap-2 flex-wrap text-xs">
                            <div className="flex gap-1">
                              {(t.numbers || []).map((n) => <Ball key={n} n={n} size={24} />)}
                            </div>
                            {(t.bonus != null || t.powerball != null) && (
                              <>
                                <span className="text-slate-300">+</span>
                                <Ball n={t.bonus ?? t.powerball} isBonus size={24} />
                              </>
                            )}
                            {t.main_matches != null && (
                              <span className="text-slate-500 ml-2">{t.main_matches} match{t.main_matches === 1 ? '' : 'es'}{t.bonus_match ? ' + bonus' : ''}</span>
                            )}
                            {tier && <span className="text-emerald-600 font-bold ml-1">· {tier.split('—')[0].trim()}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLottoGen;
